import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './AssetCard.css';
import { Skeleton } from '@mui/material';
import { faSpinner, faMicrophoneSlash } from '@fortawesome/free-solid-svg-icons';
import { useFeatures } from '../../utils/featureContext';
import { FEATURES } from '../../utils/extractFeaturesList';

const renderStatusIndicator = (loading, muted, status) => {
    if (loading) {
        return <Skeleton width={20} />;
    }
    
    if (muted === "yes") {
        return (
            <FontAwesomeIcon
                icon={faMicrophoneSlash}
                className="mute-status-icon"
                title="Muted"
            />
        );
    }
    
    return (
        <span
            className={`status-indicator ${status === "connected" ? "connected" : "disconnected"}`}
        />
    );
};

const renderMuteButton = (canMute, muteActionLabel, isMuteDisabled, showSpinner, action, isOnline, handleMuteAction) => {
    if (!canMute) {
        return null;
    }
    
    return (
        <button
            className="btn btn-download"
            onClick={handleMuteAction}
            disabled={isMuteDisabled || showSpinner || (action === "mute" && !isOnline)}
        >
            {muteActionLabel}
            {showSpinner && (
                <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="ms-2"
                />
            )}
        </button>
    );
};

const AssetCard = ({
    serialNumber,
    model,
    status,
    registration,
    locationString,
    imageSrc,
    loading,
    muted = "",
    onMuteToggle,
    onReload,
}) => {

    const [showSpinner, setShowSpinner] = useState(false);
    const { features, loading: featureLoading } = useFeatures();

    const isOnline = status === "connected";

    const canMute = !featureLoading && features?.includes(FEATURES.ASSET_MUTE);

    const muteConfig = useMemo(() => {
        const mutedState = (muted || "").toLowerCase();
        switch (mutedState) {
            case "":
            case "no":
                return {
                    displayLabel: "No",
                    muteActionLabel: "Mute",
                    confirmText: "Are you sure you want to mute this asset?",
                    isMuteDisabled: false,
                    action: "mute",
                };

            case "muting":
                return {
                    displayLabel: "Muting",
                    muteActionLabel: "Mute",
                    isMuteDisabled: true,
                };

            case "yes":
                return {
                    displayLabel: "Yes",
                    muteActionLabel: "Unmute",
                    confirmText: "Are you sure you want to unmute this asset?",
                    isMuteDisabled: false,
                    action: "unmute",
                };

            case "unmuting":
                return {
                    displayLabel: "Unmuting",
                    muteActionLabel: "Unmute",
                    isMuteDisabled: true,
                };

            default:
                return {
                    displayLabel: "-",
                    muteActionLabel: "Mute",
                    isMuteDisabled: true,
                };
        }
    }, [muted]);

    const { displayLabel, muteActionLabel, isMuteDisabled, action, confirmText } = muteConfig;

    const handleMuteAction = async () => {
        if (!action || showSpinner) return;

        // 🔒 User confirmation
        const confirmed = window.confirm(confirmText);
        if (!confirmed) return;

        try {
            setShowSpinner(true);
            await onMuteToggle?.(action);
            onReload?.();
        } catch (err) {
            console.error("Mute action failed", err);
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <div className="asset-card">
            <div className="asset-details">
                <div className="asset-field">
                    <span className="asset-label">Serial Number</span>
                    <span className="asset-value">
                        {loading ? <Skeleton width={120} height={20} /> : serialNumber}
                    </span>
                </div>
                <div className="asset-field">
                    <span className="asset-label">Model</span>
                    <span className="asset-value">
                        {loading ? <Skeleton width={80} height={20} /> : model}
                    </span>
                </div>
                <div className="asset-field">
                    <span className="asset-label">Status</span>
                    <span className="asset-value">
                        {renderStatusIndicator(loading, muted, status)}
                    </span>
                </div>

                <div className="asset-field">
                    <span className="asset-label">Registration</span>
                    <span className="asset-value">{loading ? <Skeleton width={150} height={20} /> : registration}</span>
                </div>
                <div className="asset-field">
                    <span className="asset-label">Muted</span>
                    <span className="asset-value mute-field">
                        {loading ? (
                            <Skeleton width={100} />
                        ) : (
                            <>
                                <span className="me-2">{displayLabel}</span>

                                {featureLoading ? (
                                    <Skeleton width={80} height={32} />
                                ) : (
                                    renderMuteButton(canMute, muteActionLabel, isMuteDisabled, showSpinner, action, isOnline, handleMuteAction)
                                )}
                            </>
                        )}
                    </span>
                </div>
                <div className="asset-field">
                    <span className="asset-label">Location</span>
                    <span className="asset-value">{loading ? <Skeleton width={200} height={20} /> : locationString}</span>
                </div>
            </div>
            {imageSrc && (
                <div className="asset-image">
                    {loading ? <Skeleton width={190} height={150} /> : <img src={imageSrc} alt={''} />}
                </div>
            )}
        </div>
    );
};

export default AssetCard;
